// The orb on voice mode's stage — 16 Sep 2026. What it does and why is src/orb.ts; the bubble is
// painted in WebGL (src/orb-gl.ts), or in 2D where the browser will not. How loud each side is comes
// from the voice mode's store (`levels`), read every frame, never through React state — sixty
// renders a second of the whole panel would be the price of that.
//
// WHERE THERE IS NO READING — the local loop, the phone check's scripted voice, a browser that
// will not analyse the call — she still speaks on a made-up voice level (`speechLike`), and a
// listening orb breathes rather than guessing when the recruiter talks.
//
// UNDER REDUCED MOTION it does not animate: one still frame of each state, drawn when the state,
// the colours or the canvas side change (app.css's rule for every loop in the brain), on events
// and never a poll (T433a); flipping the setting mid-session switches between the two. The moving
// orb's loop stops while it is off-screen or the tab is hidden (T433a: the brand orb outlives
// every page, and its WebGL context ran for the whole session).
import { useEffect, useRef, useState } from 'react';
import { speechLike } from '../audio-level.ts';
import { createOrbGl, type OrbGl } from '../orb-gl.ts';
import {
  drawOrb2d,
  OrbMotion,
  paletteOf,
  stillFrameStale,
  watchPixelRatio,
  type OrbLevels,
  type OrbPalette,
  type OrbState,
} from '../orb.ts';

/**
 * The side the orb is drawn at, in CSS pixels: its big size in the middle of the stage. At the top the
 * browser scales the same canvas down, so the move between the two stays smooth (styles/screens/find.css).
 */
export const VOICE_ORB_SIZE = 196;

const SILENT: OrbLevels = { input: 0, output: 0 };

const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';

function prefersStill(): boolean {
  return window.matchMedia?.(REDUCED_MOTION).matches ?? false;
}

/**
 * `size` is the canvas's side in CSS pixels too, when it is not the stage's (the big orb by
 * default); `className` lets another place style it — T433 (23 Oct 2026): the brand mark at the top left of the brain and the
 * sign-in page is this orb, small, in the picked colour.
 */
export function VoiceOrb({
  state,
  levels,
  size = VOICE_ORB_SIZE,
  className = 'voice__orb',
}: {
  state: OrbState;
  levels?: () => OrbLevels | null;
  size?: number;
  className?: string;
}) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const latest = useRef({ state, levels });
  latest.current = { state, levels };
  // A canvas once asked for WebGL cannot draw in 2D, so falling back means a new canvas (the key).
  const [painter, setPainter] = useState<'gl' | '2d'>('gl');
  // T433a: the reduced-motion setting, followed as it changes rather than read once.
  const [still, setStill] = useState(prefersStill);
  useEffect(() => {
    const media = window.matchMedia?.(REDUCED_MOTION);
    if (!media) return;
    const follow = () => setStill(media.matches);
    media.addEventListener('change', follow);
    follow();
    return () => media.removeEventListener('change', follow);
  }, []);
  // A still orb repaints when the state it is given changes.
  const repaint = useRef<(() => void) | null>(null);
  useEffect(() => {
    repaint.current?.();
  }, [state]);

  useEffect(() => {
    const element = canvas.current;
    if (!element) return;
    let gl: OrbGl | null = null;
    let flat: CanvasRenderingContext2D | null = null;
    if (painter === 'gl') {
      gl = createOrbGl(element, () => setPainter('2d'));
      if (!gl) {
        setPainter('2d');
        return;
      }
    } else {
      flat = element.getContext('2d');
      if (!flat) return;
    }
    let palette = paletteOf(getComputedStyle(element));
    const sideOf = () => Math.round(size * Math.min(3, window.devicePixelRatio || 1));
    // A theme or accent switch changes the tokens under a live orb.
    const themes = new MutationObserver(() => {
      palette = paletteOf(getComputedStyle(element));
      repaint.current?.();
    });
    themes.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme', 'data-accent'],
    });
    const paint = (motion: OrbMotion) => {
      const ratio = Math.min(3, window.devicePixelRatio || 1);
      const side = sideOf();
      if (element.width !== side) {
        element.width = side;
        element.height = side;
      }
      const shown = motion.frame();
      if (gl) {
        gl.render(shown, palette, side);
      } else if (flat) {
        flat.setTransform(ratio, 0, 0, ratio, 0, 0);
        drawOrb2d(flat, size, shown, palette);
      }
    };

    if (still) {
      let drawn: { state: OrbState; palette: OrbPalette; side: number } | null = null;
      const paintStill = () => {
        const { state: now } = latest.current;
        if (!stillFrameStale(drawn, { state: now, palette, side: sideOf() })) return;
        drawn = { state: now, palette, side: sideOf() };
        const motion = new OrbMotion(() => 0.5);
        motion.step(16, now, SILENT);
        paint(motion);
      };
      repaint.current = paintStill;
      paintStill();
      // A zoom resizes the window; another display changes only the pixel ratio (T442a).
      window.addEventListener('resize', paintStill);
      const stopWatching = watchPixelRatio(window, paintStill);
      return () => {
        repaint.current = null;
        window.removeEventListener('resize', paintStill);
        stopWatching();
        themes.disconnect();
        gl?.dispose();
      };
    }

    const motion = new OrbMotion();
    const madeUp = speechLike();
    let request = 0;
    let last = performance.now();
    const tick = (now: number) => {
      const { state: current, levels: read } = latest.current;
      const heard = read?.() ?? null;
      motion.step(
        now - last,
        current,
        heard ?? { input: 0, output: current === 'speaking' ? madeUp(now) : 0 },
      );
      last = now;
      paint(motion);
      request = window.requestAnimationFrame(tick);
    };
    // T433a: it runs only while it can be seen: on a page of the tab in front.
    let onScreen = true;
    const sync = () => {
      const should = onScreen && !document.hidden;
      if (should && request === 0) {
        last = performance.now();
        request = window.requestAnimationFrame(tick);
      } else if (!should && request !== 0) {
        window.cancelAnimationFrame(request);
        request = 0;
      }
    };
    const seen = new IntersectionObserver((entries) => {
      onScreen = entries.at(-1)?.isIntersecting ?? true;
      sync();
    });
    seen.observe(element);
    document.addEventListener('visibilitychange', sync);
    sync();
    return () => {
      window.cancelAnimationFrame(request);
      seen.disconnect();
      document.removeEventListener('visibilitychange', sync);
      themes.disconnect();
      gl?.dispose();
    };
  }, [painter, size, still]);

  return (
    <canvas
      key={painter}
      ref={canvas}
      className={className}
      width={size}
      height={size}
      style={size === VOICE_ORB_SIZE ? undefined : { width: size, height: size }}
      aria-hidden
    />
  );
}
